<!--
  NotificationToggle — Phase 6 / M5.3.

  Header chip showing the current Notification.permission state. Three
  visual states:
    - default → "Enable notifications" (clickable, triggers the subscribe
      flow). Browsers require a user gesture to call requestPermission()
      so this MUST be a click handler — not a $effect.
    - granted → "Notifications on" (subtle, non-interactive). Hidden after
      first display unless user mouse-hovers — we don't want a permanent
      "we're listening" indicator in the chrome.
    - denied → "Notifications blocked" with a tooltip pointing at browser
      settings. Browsers don't allow re-requesting permission after a
      denial, only the user can flip it back via site settings.

  Renders nothing when:
    - The browser lacks PushManager / Notification (very old / SSR).
    - The widget is running embedded in an iframe whose host hasn't
      delegated notifications permission (Permissions API tells us this
      via permission.state === 'denied' even before any prompt).

  Phase 6 / M5.2: also hosts the in-app notification-sound mute toggle.
  Sound is independent of push permission (it plays in-page while the
  widget tab is foregrounded), so the sound button renders regardless
  of permission state — including when notification permission is
  unsupported entirely. The two controls are visually grouped in one
  inline-flex container so parent layouts treat them as a single chip.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { registerServiceWorker } from '../lib/push/sw-registration.js';
  import { ensureSubscription } from '../lib/push/push-subscription.js';
  import { notificationPrefs } from '../lib/stores/notification-prefs.svelte.js';

  type Permission = 'default' | 'granted' | 'denied' | 'unsupported';

  let permission = $state<Permission>('unsupported');
  let pending = $state(false);
  let lastError = $state<string | null>(null);

  function readPermission(): Permission {
    if (typeof Notification === 'undefined' || typeof navigator === 'undefined') {
      return 'unsupported';
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return 'unsupported';
    }
    return Notification.permission as Permission;
  }

  onMount(() => {
    permission = readPermission();
  });

  // M5.2 sound mute, account-level since M5.6.5. The store flips the
  // value immediately and reconciles with the server behind the click,
  // so this handler stays fire-and-forget — the chip must not sit
  // disabled waiting on a round trip for a mute.
  const soundEnabled = $derived(notificationPrefs.state.soundEnabled);
  function toggleSound(): void {
    void notificationPrefs.toggleSound();
  }

  async function onEnableClick() {
    if (pending) return;
    pending = true;
    lastError = null;
    try {
      // requestPermission must be inside the click handler call stack —
      // browsers reject it if invoked from a microtask scheduled by an
      // earlier handler. Awaits after this line are fine.
      const granted = await Notification.requestPermission();
      permission = granted as Permission;
      if (granted !== 'granted') return;

      const reg = await registerServiceWorker();
      if (!reg) {
        lastError = 'Could not register service worker.';
        return;
      }
      await ensureSubscription(reg);
      // M5.6.5 — clicking "Enable notifications" is an explicit yes, so
      // it also lifts the account-level mute. Without this an operator
      // who turned push off in the portal would grant browser permission
      // here and still receive nothing: the server would drop every
      // event at the pushEnabled gate, with no visible reason why.
      await notificationPrefs.setPushEnabled(true);
    } catch (err) {
      lastError = err instanceof Error ? err.message : 'Subscription failed.';
    } finally {
      pending = false;
    }
  }
</script>

<div class="group">
  {#if permission === 'default'}
    <button
      type="button"
      class="toggle enable"
      disabled={pending}
      onclick={onEnableClick}
      title={lastError ?? 'Get a desktop notification when a new message arrives.'}
      aria-label="Enable notifications"
    >
      {pending ? 'Enabling…' : 'Enable notifications'}
    </button>
  {:else if permission === 'denied'}
    <span
      class="toggle blocked"
      title="Notifications are blocked. Unblock in your browser settings to re-enable."
      aria-label="Notifications blocked"
    >
      Notifications blocked
    </span>
  {:else if permission === 'granted'}
    <span class="toggle on" aria-label="Notifications enabled">Notifications on</span>
  {/if}

  <button
    type="button"
    class="sound-toggle"
    class:muted={!soundEnabled}
    onclick={toggleSound}
    title={soundEnabled ? 'Mute in-app sound on new messages' : 'Unmute in-app sound on new messages'}
    aria-label={soundEnabled ? 'Sound on. Click to mute.' : 'Sound off. Click to unmute.'}
    aria-pressed={!soundEnabled}
  >
    <span aria-hidden="true">{soundEnabled ? '🔊' : '🔇'}</span>
  </button>
</div>

<style>
  .group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .sound-toggle {
    height: 26px;
    width: 26px;
    padding: 0;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .sound-toggle:hover {
    background: var(--widget-bg-sunk, oklch(96% 0.004 250));
  }
  .sound-toggle.muted {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    opacity: 0.65;
  }
  .sound-toggle:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }

  .toggle {
    height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    font-family: var(--widget-font);
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    border: 1px solid transparent;
    background: transparent;
    cursor: default;
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .toggle.enable {
    cursor: pointer;
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 12%, transparent);
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 28%, transparent);
  }
  .toggle.enable:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 18%, transparent);
    border-color: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 42%, transparent);
  }
  .toggle.enable:disabled {
    opacity: 0.65;
    cursor: progress;
  }
  .toggle.enable:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .toggle.on {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    background: var(--widget-bg-sunk, oklch(96% 0.004 250));
  }
  .toggle.blocked {
    color: var(--widget-danger, oklch(55% 0.2 25));
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    border-color: color-mix(in oklch, var(--widget-danger, oklch(55% 0.2 25)) 30%, transparent);
  }
</style>
